/* eslint-disable */
import React from 'react';
import { Box, Typography } from '@mui/material';
import type { Ticket } from '../../types/release';
import { TicketBadge } from '../common/TicketBadge';

interface RDActivitiesProps {
  rdActivities: Ticket[];
}

export const RDActivities: React.FC<RDActivitiesProps> = ({ rdActivities }) => {
  if (rdActivities.length === 0) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          R&D Activities
        </Typography>
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No R&D activities found for this release.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Try adjusting your filters or search query.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Create a set of all ticket IDs to check for parent relationships
  const allTicketIds = new Set(rdActivities.map(t => t.id));
  
  // Also collect all child IDs from children arrays and tasks arrays
  const childIds = new Set<string>();
  rdActivities.forEach(ticket => {
    if (ticket.children) {
      ticket.children.forEach((child: any) => {
        if (typeof child === 'object' && child.id) {
          childIds.add(child.id);
        }
      });
    }
    if (ticket.tasks) {
      ticket.tasks.forEach((task: any) => {
        if (typeof task === 'object' && task.id) {
          childIds.add(task.id);
        }
      });
    }
  });
  
  // Filter out tickets that are children of other tickets in the list
  // (they should only appear nested under their parent)
  const topLevelTickets = rdActivities.filter(ticket => {
    // If ticket has a parent_id, check if that parent is also in the list
    if (ticket.parent_id && allTicketIds.has(ticket.parent_id)) {
      return false; // This is a child, exclude it from top-level
    }
    // Also exclude if it's in the children/tasks arrays of other tickets
    if (childIds.has(ticket.id)) {
      return false;
    }
    return true; // Top-level ticket (no parent or parent not in list)
  });

  // Helper to recursively find and render children
  const renderChildren = (parentId: string, level: number = 1): React.ReactNode => {
    // Find all tickets that are children of this parent
    const childTickets = rdActivities.filter(t => t.parent_id === parentId);
    
    if (childTickets.length === 0) {
      return null;
    }

    return (
      <Box sx={{ ml: 4, mt: 1 }}>
        {childTickets.map((childTicket) => (
          <Box key={childTicket.id} sx={{ mb: 1 }}>
            <TicketBadge ticket={childTicket} />
            {/* Recursively render grandchildren */}
            {renderChildren(childTicket.id, level + 1)}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        R&D Activities ({topLevelTickets.length})
      </Typography>
      
      {topLevelTickets.map((ticket) => {
        // For Epics, check tasks field instead of children
        const hasDirectChildren = ticket.type === 'Epic' 
          ? ticket.tasks && ticket.tasks.length > 0
          : ticket.children && ticket.children.length > 0;
        
        // Also check if there are tickets in the list that have this ticket as parent
        const hasChildTickets = rdActivities.some(t => t.parent_id === ticket.id);
        
        return (
          <Box key={ticket.id} sx={{ mb: 2 }}>
            <TicketBadge ticket={ticket} />
            
            {/* Render direct children from children array (simplified objects) */}
            {hasDirectChildren && (
              <Box sx={{ ml: 4, mt: 1 }}>
                {(ticket.type === 'Epic' && ticket.tasks
                  ? ticket.tasks
                  : ticket.children || []
                ).map((child) => {
                  // Handle simplified child objects (like in CustomerValue)
                  const childTicket: Ticket = typeof child === 'object' && 'type' in child
                    ? child as Ticket
                    : {
                        ...child,
                        type: 'Sub-task',
                        labels: [],
                        link: `https://outsight.atlassian.net/browse/${child.id}`,
                        children: [],
                        is_late: child.is_late || false,
                        validation_issues: [],
                      };
                  return <TicketBadge key={child.id} ticket={childTicket} />;
                })}
              </Box>
            )}
            
            {/* Render child tickets from the list (full Ticket objects with parent_id) */}
            {hasChildTickets && renderChildren(ticket.id)}
          </Box>
        );
      })}
    </Box>
  );
};
